// Map Utility Functions

import L from 'leaflet';
// @ts-ignore
import polyline from '@mapbox/polyline';
import { getVehicleColorById, VEHICLE_ROUTE_COLORS } from '../../../../api/mobiApi/constants/colors';


// Polyline decode with Mapbox library
export const decodePolyline = (encoded: string): [number, number][] => {
  try {
    const coords = polyline.decode(encoded, 6);
    if (coords.length === 0) return [];
    return coords.map((coord: [number, number]) => [coord[0], coord[1]] as [number, number]);
  } catch (error) {
    console.error('Polyline decode failed:', error);
    return [];
  }
};

// Apply jitter to coordinates to avoid overlapping markers
export const applyJitter = (lat: number, lon: number, count: number): [number, number] => {
  if (count <= 0) return [lat, lon];
  const delta = 0.00008 * count;
  return [lat + delta, lon + delta];
};

// Validate coordinate
export const isValidCoord = (coord: any): boolean => {
  return (
    Array.isArray(coord) &&
    coord.length === 2 &&
    typeof coord[0] === 'number' &&
    typeof coord[1] === 'number' &&
    !isNaN(coord[0]) &&
    !isNaN(coord[1]) &&
    coord[0] >= -90 &&
    coord[0] <= 90 &&
    coord[1] >= -180 &&
    coord[1] <= 180
  );
};

// Re-export for backward compatibility
export const ROUTE_COLORS = VEHICLE_ROUTE_COLORS;

export const getVehicleColor = (vehicleId: number | string): string => {
  return getVehicleColorById(vehicleId);
};

// Create numbered customer icon (pin style) - Modern 3D Design
export const createCustomerIcon = (number: number, color: string) => {
  return new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
        <defs>
          <linearGradient id="grad-${number}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${color};stop-opacity:0.7" />
          </linearGradient>
          <filter id="shadow-${number}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="0" dy="3" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.4"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <radialGradient id="shine-${number}">
            <stop offset="0%" style="stop-color:white;stop-opacity:0.8" />
            <stop offset="100%" style="stop-color:white;stop-opacity:0" />
          </radialGradient>
        </defs>
        <!-- Pin body with gradient -->
        <path d="M20 0 C9 0 0 9 0 20 C0 31 20 52 20 52 C20 52 40 31 40 20 C40 9 31 0 20 0 Z"
              fill="url(#grad-${number})" stroke="white" stroke-width="3" filter="url(#shadow-${number})"/>
        <!-- Inner circle for number -->
        <circle cx="20" cy="20" r="12" fill="white" opacity="0.95"/>
        <!-- Shine effect -->
        <ellipse cx="20" cy="15" rx="8" ry="6" fill="url(#shine-${number})" opacity="0.3"/>
        <!-- Number -->
        <text x="20" y="26" text-anchor="middle" font-size="16" fill="${color}" font-weight="bold" font-family="Arial, sans-serif">${number}</text>
      </svg>
    `),
    iconSize: [36, 46],
    iconAnchor: [18, 46],
    popupAnchor: [0, -46],
  });
};

// Start Depot icon (warehouse pin) - Green for Start
export const depotIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="46" height="60" viewBox="0 0 46 60">
      <defs>
        <linearGradient id="start-depot-grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#10b981;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#059669;stop-opacity:1" />
        </linearGradient>
        <filter id="shadow-start-depot" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
          <feOffset dx="0" dy="3" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.5"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <radialGradient id="start-depot-shine">
          <stop offset="0%" style="stop-color:white;stop-opacity:0.6" />
          <stop offset="100%" style="stop-color:white;stop-opacity:0" />
        </radialGradient>
      </defs>
      <!-- Pin body -->
      <path d="M23 0 C10.3 0 0 10.3 0 23 C0 35.7 23 60 23 60 C23 60 46 35.7 46 23 C46 10.3 35.7 0 23 0 Z"
            fill="url(#start-depot-grad)" stroke="white" stroke-width="3.5" filter="url(#shadow-start-depot)"/>
      <!-- Shine effect -->
      <ellipse cx="23" cy="18" rx="12" ry="8" fill="url(#start-depot-shine)" opacity="0.4"/>
      <!-- Warehouse building -->
      <g transform="translate(12, 12)">
        <!-- Building body -->
        <rect x="1" y="6" width="20" height="14" fill="white" rx="2" opacity="0.95"/>
        <!-- Roof -->
        <path d="M0 7 L11 1 L22 7 Z" fill="white" opacity="0.95"/>
        <!-- Door -->
        <rect x="8" y="13" width="6" height="7" fill="#059669" rx="1"/>
        <!-- Windows -->
        <rect x="3" y="9" width="3" height="3" fill="#059669" rx="0.5" opacity="0.8"/>
        <rect x="16" y="9" width="3" height="3" fill="#059669" rx="0.5" opacity="0.8"/>
        <!-- Door handle -->
        <circle cx="12" cy="16" r="0.8" fill="white"/>
      </g>
    </svg>
  `),
  iconSize: [42, 54],
  iconAnchor: [21, 54],
  popupAnchor: [0, -54],
});

// End Depot icon (warehouse pin) - Red for End
export const endDepotIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="46" height="60" viewBox="0 0 46 60">
      <defs>
        <linearGradient id="end-depot-grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#ef4444;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#dc2626;stop-opacity:1" />
        </linearGradient>
        <filter id="shadow-end-depot" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
          <feOffset dx="0" dy="3" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.5"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <radialGradient id="end-depot-shine">
          <stop offset="0%" style="stop-color:white;stop-opacity:0.6" />
          <stop offset="100%" style="stop-color:white;stop-opacity:0" />
        </radialGradient>
      </defs>
      <!-- Pin body -->
      <path d="M23 0 C10.3 0 0 10.3 0 23 C0 35.7 23 60 23 60 C23 60 46 35.7 46 23 C46 10.3 35.7 0 23 0 Z"
            fill="url(#end-depot-grad)" stroke="white" stroke-width="3.5" filter="url(#shadow-end-depot)"/>
      <!-- Shine effect -->
      <ellipse cx="23" cy="18" rx="12" ry="8" fill="url(#end-depot-shine)" opacity="0.4"/>
      <!-- Warehouse building -->
      <g transform="translate(12, 12)">
        <!-- Building body -->
        <rect x="1" y="6" width="20" height="14" fill="white" rx="2" opacity="0.95"/>
        <!-- Roof -->
        <path d="M0 7 L11 1 L22 7 Z" fill="white" opacity="0.95"/>
        <!-- Door -->
        <rect x="8" y="13" width="6" height="7" fill="#dc2626" rx="1"/>
        <!-- Windows -->
        <rect x="3" y="9" width="3" height="3" fill="#dc2626" rx="0.5" opacity="0.8"/>
        <rect x="16" y="9" width="3" height="3" fill="#dc2626" rx="0.5" opacity="0.8"/>
        <!-- Door handle -->
        <circle cx="12" cy="16" r="0.8" fill="white"/>
      </g>
    </svg>
  `),
  iconSize: [42, 54],
  iconAnchor: [21, 54],
  popupAnchor: [0, -54],
});

// Customer icon (simple pin without number) - Modern with Pulse Effect
export const createSimpleCustomerIcon = () => {
  return new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="38" height="50" viewBox="0 0 38 50">
        <defs>
          <linearGradient id="customer-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#60a5fa;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#3b82f6;stop-opacity:1" />
          </linearGradient>
          <filter id="shadow-customer" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="0" dy="3" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.4"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <radialGradient id="customer-shine">
            <stop offset="0%" style="stop-color:white;stop-opacity:0.8" />
            <stop offset="100%" style="stop-color:white;stop-opacity:0" />
          </radialGradient>
        </defs>
        <!-- Pulse rings -->
        <circle cx="19" cy="19" r="16" fill="none" stroke="#3b82f6" stroke-width="1.5" opacity="0.3"/>
        <circle cx="19" cy="19" r="13" fill="none" stroke="#3b82f6" stroke-width="1" opacity="0.5"/>
        <!-- Pin body -->
        <path d="M19 0 C8.5 0 0 8.5 0 19 C0 29.5 19 50 19 50 C19 50 38 29.5 38 19 C38 8.5 29.5 0 19 0 Z"
              fill="url(#customer-grad)" stroke="white" stroke-width="3" filter="url(#shadow-customer)"/>
        <!-- Shine effect -->
        <ellipse cx="19" cy="14" rx="9" ry="7" fill="url(#customer-shine)" opacity="0.4"/>
        <!-- Inner icon (location dot) -->
        <circle cx="19" cy="19" r="7" fill="white" opacity="0.95"/>
        <circle cx="19" cy="19" r="4" fill="#3b82f6"/>
      </svg>
    `),
    iconSize: [34, 44],
    iconAnchor: [17, 44],
    popupAnchor: [0, -44],
  });
};

// Break icon (coffee cup pin) - Modern Coffee Design
export const breakIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="56" viewBox="0 0 42 56">
      <defs>
        <linearGradient id="break-grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#34d399;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#22c55e;stop-opacity:1" />
        </linearGradient>
        <filter id="shadow-break" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
          <feOffset dx="0" dy="3" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.4"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <radialGradient id="break-shine">
          <stop offset="0%" style="stop-color:white;stop-opacity:0.7" />
          <stop offset="100%" style="stop-color:white;stop-opacity:0" />
        </radialGradient>
      </defs>
      <!-- Pin body -->
      <path d="M21 0 C9.4 0 0 9.4 0 21 C0 32.6 21 56 21 56 C21 56 42 32.6 42 21 C42 9.4 32.6 0 21 0 Z"
            fill="url(#break-grad)" stroke="white" stroke-width="3" filter="url(#shadow-break)"/>
      <!-- Shine effect -->
      <ellipse cx="21" cy="16" rx="10" ry="7" fill="url(#break-shine)" opacity="0.4"/>
      <!-- Coffee cup -->
      <g transform="translate(13, 13)">
        <!-- Steam lines -->
        <path d="M5 1 Q6 -1 7 1" stroke="white" stroke-width="1.5" fill="none" opacity="0.8" stroke-linecap="round"/>
        <path d="M8 0 Q9 -2 10 0" stroke="white" stroke-width="1.5" fill="none" opacity="0.8" stroke-linecap="round"/>
        <!-- Cup body -->
        <rect x="1" y="4" width="12" height="9" fill="white" rx="2" opacity="0.95"/>
        <!-- Coffee inside -->
        <rect x="2" y="5" width="10" height="6" fill="#22c55e" rx="1.5" opacity="0.7"/>
        <!-- Handle -->
        <path d="M13 6 Q16 6 16 9 Q16 12 13 12" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round"/>
        <!-- Saucer -->
        <ellipse cx="7" cy="13.5" rx="8" ry="2" fill="white" opacity="0.9"/>
      </g>
    </svg>
  `),
  iconSize: [38, 50],
  iconAnchor: [19, 50],
  popupAnchor: [0, -50],
});


// Map types configuration
export const MAP_TYPES = {
  osm: {
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  },
  osm_night: {
    name: 'OpenStreetMap Night',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  carto_light: {
    name: 'CartoDB Light',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  carto_dark: {
    name: 'CartoDB Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  carto_voyager: {
    name: 'CartoDB Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  },
  wikimedia: {
    name: 'Wikimedia',
    url: 'https://maps.wikimedia.org/osm-intl/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://wikimediafoundation.org/">Wikimedia</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  },
  cyclosm: {
    name: 'CyclOSM',
    url: 'https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.cyclosm.org/">CyclOSM</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  },
  esri_world: {
    name: 'Esri World',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
  },
  esri_street: {
    name: 'Esri Street',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012'
  }
};
